/**
 * NewAppointmentModal — single unified create flow for the FSR.
 *
 * Replaces the previous fragmented "Step1 → Step5" stub flow on the
 * officer side. The customer-facing 5-step wizard (BookingWizard) is
 * deliberately UNCHANGED — that's the public booking experience and
 * stays intact.
 *
 * Channels:
 *   - In-branch     → calendar event with branch as location
 *   - Phone         → calendar event with "Phone" as location
 *   - Teams meeting → online meeting + calendar event with join link
 *   - Bookings link → returns the public Bookings URL to share with customer
 *
 * In demo mode (no LIVE flag) the create call is simulated end-to-end;
 * this keeps Bill's "no stubs, no confusion" mandate but still shows
 * the FSR a confirmation toast.
 */

import { useMemo, useState } from 'react';
import { ModalShell, Button, Pill } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useNewAppointment } from '../hooks/useNewAppointment';
import type { NewAppointmentChannel, NewAppointmentInput } from '../types';

const CHANNEL_OPTIONS: ReadonlyArray<{
  value: NewAppointmentChannel;
  icon: string;
  title: string;
  micro: string;
}> = [
  { value: 'inBranch', icon: '🏢', title: 'In-branch', micro: "Customer comes to the branch." },
  { value: 'phone', icon: '📞', title: 'Phone', micro: "We call the customer." },
  { value: 'teams', icon: '💻', title: 'Teams meeting', micro: "Generates a Teams join link." },
  { value: 'bookingsLink', icon: '📅', title: 'Bookings link', micro: "Send the customer a self-book link." },
];

const SUBJECT_PRESETS: ReadonlyArray<string> = [
  'Open new account',
  'CD rate review',
  'IRA opening',
  'Address update',
  'Loan inquiry',
  'Account review',
  'SDB close-out',
  'Beneficiary update',
  'Other',
];

const DURATION_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hr' },
  { value: 90, label: '1.5 hr' },
];

export interface NewAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export function NewAppointmentModal({ open, onClose, onCreated }: NewAppointmentModalProps): JSX.Element {
  const { user } = useAuth();
  const { config } = useConfig();
  const { busy, create } = useNewAppointment();

  const defaultBranch = user?.activeBranchCode ?? user?.branchCode ?? config.branches[0]?.branchCode ?? '';

  const todayIso = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
  }, []);

  const [channel, setChannel] = useState<NewAppointmentChannel>('inBranch');
  const [customerName, setCustomerName] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [subject, setSubject] = useState<string>('Open new account');
  const [date, setDate] = useState<string>(todayIso);
  const [time, setTime] = useState<string>('09:00');
  const [duration, setDuration] = useState<number>(45);
  const [branchCode, setBranchCode] = useState<string>(defaultBranch);
  const [notes, setNotes] = useState<string>('');
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [resultOk, setResultOk] = useState<boolean | null>(null);

  function reset(): void {
    setChannel('inBranch');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setSubject('Open new account');
    setDate(todayIso);
    setTime('09:00');
    setDuration(45);
    setBranchCode(defaultBranch);
    setNotes('');
    setResultMsg(null);
    setResultOk(null);
  }

  function close(): void {
    reset();
    onClose();
  }

  async function submit(): Promise<void> {
    const input: NewAppointmentInput = {
      customerName: customerName.trim() || 'Customer',
      subject: subject.trim() || 'Appointment',
      channel,
      date,
      time,
      durationMin: duration,
      branchCode,
    };
    if (customerEmail.trim()) input.customerEmail = customerEmail.trim();
    if (customerPhone.trim()) input.customerPhone = customerPhone.trim();
    if (notes.trim()) input.notes = notes.trim();

    const res = await create(input);
    setResultOk(res.ok);
    setResultMsg(res.message);
    if (res.ok && onCreated) onCreated();
  }

  return (
    <ModalShell
      open={open}
      onClose={close}
      title="New appointment"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={close}>Close</Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={busy || !customerName.trim() || !subject.trim() || !branchCode}
          >
            {busy ? 'Creating…' : channel === 'bookingsLink' ? 'Get Bookings link' : 'Create appointment'}
          </Button>
        </>
      }
    >
      <fieldset className="appt-modal__channel">
        <legend>Channel</legend>
        <div className="appt-modal__channel-grid">
          {CHANNEL_OPTIONS.map((opt) => {
            const isSel = channel === opt.value;
            return (
              <label
                key={opt.value}
                className={`appt-modal__channel-card ${isSel ? 'is-selected' : ''}`.trim()}
              >
                <input
                  type="radio"
                  name="appt-channel"
                  value={opt.value}
                  checked={isSel}
                  onChange={() => setChannel(opt.value)}
                />
                <span className="appt-modal__channel-icon" aria-hidden>{opt.icon}</span>
                <span className="appt-modal__channel-title">{opt.title}</span>
                <span className="appt-modal__channel-micro">{opt.micro}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="appt-modal__row">
        <label className="appt-modal__field">
          <span>Customer name</span>
          <input
            type="text"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="e.g. Connor Sample"
            autoComplete="name"
          />
        </label>
        <label className="appt-modal__field">
          <span>Email <span className="appt-modal__opt">(optional)</span></span>
          <input
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="customer@example.com"
            autoComplete="email"
          />
        </label>
      </div>

      <div className="appt-modal__row">
        <label className="appt-modal__field">
          <span>Phone <span className="appt-modal__opt">(optional)</span></span>
          <input
            type="tel"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            placeholder="(573) 555-0123"
            autoComplete="tel"
          />
        </label>
        <label className="appt-modal__field">
          <span>Subject</span>
          <select value={subject} onChange={(e) => setSubject(e.target.value)}>
            {SUBJECT_PRESETS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="appt-modal__row">
        <label className="appt-modal__field">
          <span>Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="appt-modal__field">
          <span>Time</span>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} step={900} />
        </label>
        <label className="appt-modal__field">
          <span>Duration</span>
          <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
            {DURATION_OPTIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="appt-modal__row">
        <label className="appt-modal__field appt-modal__field--full">
          <span>Branch</span>
          <select value={branchCode} onChange={(e) => setBranchCode(e.target.value)}>
            <option value="">Choose a branch…</option>
            {config.branches.filter((b) => b.isActive).map((b) => (
              <option key={b.branchCode} value={b.branchCode}>{b.branchName}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="appt-modal__field appt-modal__field--full">
        <span>Notes <span className="appt-modal__opt">(optional)</span></span>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything the officer should know"
        />
      </label>

      {resultMsg && (
        <div
          role="status"
          className={`appt-modal__result ${resultOk ? 'is-ok' : 'is-err'}`.trim()}
        >
          <Pill variant={resultOk ? 'ok' : 'err'}>{resultOk ? 'Done' : 'Error'}</Pill>
          <span>{resultMsg}</span>
        </div>
      )}
    </ModalShell>
  );
}
